/**
 * Application Entry Point
 *
 * Sets up:
 * - Privy for wallet authentication (when VITE_PRIVY_APP_ID is set)
 * - wagmi for Ethereum interactions
 * - React Query (TanStack Query) for data fetching
 * - TanStack Router for navigation
 * - Toast notifications provider
 * - React StrictMode for development checks
 *
 * In E2E test mode (VITE_TEST_MODE=true), Privy/wagmi are replaced with
 * MockWalletProvider so Playwright can control wallet state via
 * window.__TEST_WALLET_STATE__ without needing a real MetaMask extension.
 */

import { PrivyProvider } from "@privy-io/react-auth";
import { WagmiProvider } from "@privy-io/wagmi";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { type ReactNode, StrictMode } from "react";
import { createRoot } from "react-dom/client";

import { ToastProvider } from "./components/ui/Toast";
import { PrivyWalletProvider } from "./hooks/PrivyWalletProvider";
import { MockWalletProvider } from "./hooks/use-wallet";
import { getActiveChain, wagmiConfig } from "./lib/wagmi-config";
import { router } from "./routes";
import "./index.css";

/**
 * Create QueryClient with sensible defaults
 */
const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: 60 * 1000,
			retry: 1,
			refetchOnWindowFocus: import.meta.env.PROD,
		},
	},
});

const activeChain = getActiveChain();
const privyAppId = import.meta.env.VITE_PRIVY_APP_ID;
const testMode = import.meta.env.VITE_TEST_MODE === "true";

/**
 * Wraps children with the appropriate wallet provider:
 * - Test mode: MockWalletProvider (reads window.__TEST_WALLET_STATE__)
 * - Production with Privy: PrivyProvider + WagmiProvider + PrivyWalletProvider
 * - Dev without Privy: plain QueryClientProvider (useWallet returns disconnected fallback)
 */
function Providers({ children }: { children: ReactNode }) {
	if (testMode) {
		return (
			<QueryClientProvider client={queryClient}>
				<MockWalletProvider>{children}</MockWalletProvider>
			</QueryClientProvider>
		);
	}

	if (privyAppId) {
		return (
			<PrivyProvider
				appId={privyAppId}
				config={{
					loginMethods: ["wallet", "email", "google"],
					appearance: {
						theme: "dark",
					},
					defaultChain: activeChain,
					supportedChains: [activeChain],
				}}
			>
				<QueryClientProvider client={queryClient}>
					<WagmiProvider config={wagmiConfig}>
						<PrivyWalletProvider>{children}</PrivyWalletProvider>
					</WagmiProvider>
				</QueryClientProvider>
			</PrivyProvider>
		);
	}

	return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
}

const rootElement = document.getElementById("root");
// eslint-disable-next-line no-restricted-syntax -- React bootstrap requires DOM element
if (!rootElement) throw new Error("Root element not found");

createRoot(rootElement).render(
	<StrictMode>
		<Providers>
			<ToastProvider>
				<RouterProvider router={router} />
			</ToastProvider>
		</Providers>
	</StrictMode>
);
